/**
 * Meilensteine und Notizen eines Projekts (F-10). Meilensteine leben als
 * Array im Projekt - sie werden immer mit ihm gelesen und geschrieben, wie
 * Key Results im Ziel.
 */
import { db } from '@/db/schema';
import { inDenPapierkorb, papierkorbTitel } from '@/db/repositories/papierkorb';
import { nowInstant } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';
import type { ID, LocalDate, Milestone } from '@/domain/types';

/** Nach Datum sortiert - so liest sich die Planung als Zeitleiste. */
function sortiert(liste: Milestone[]): Milestone[] {
  return [...liste].sort((a, b) => a.date.localeCompare(b.date) || a.title.localeCompare(b.title, 'de'));
}

async function aendere(projectId: ID, fn: (liste: Milestone[]) => Milestone[]): Promise<void> {
  await db.projects.where('id').equals(projectId).modify((p) => {
    p.milestones = sortiert(fn(p.milestones ?? []));
    p.updatedAt = nowInstant();
  });
}

export async function addMilestone(projectId: ID, title: string, date: LocalDate): Promise<void> {
  const t = title.trim();
  if (!t || !date) return;
  await aendere(projectId, (l) => [...l, { id: newId(), title: t, date, done: false }]);
}

export async function toggleMilestone(projectId: ID, milestoneId: ID): Promise<void> {
  await aendere(projectId, (l) => l.map((m) => (m.id === milestoneId ? { ...m, done: !m.done } : m)));
}

export async function updateMilestone(projectId: ID, milestoneId: ID, patch: Partial<Pick<Milestone, 'title' | 'date'>>): Promise<void> {
  await aendere(projectId, (l) => l.map((m) => (m.id === milestoneId ? { ...m, ...patch } : m)));
}

export async function removeMilestone(projectId: ID, milestoneId: ID): Promise<void> {
  const m = (await db.projects.get(projectId))?.milestones?.find((x) => x.id === milestoneId);
  await inDenPapierkorb({ art: 'entfernt', tabelle: 'projects', zeileIds: [projectId], titel: papierkorbTitel(m?.title) },
    () => aendere(projectId, (l) => l.filter((x) => x.id !== milestoneId)));
}

/** Leere Notiz entfernt das Feld. */
export async function setProjectNotes(projectId: ID, notes: string): Promise<void> {
  const text = notes.trim();
  await db.projects.where('id').equals(projectId).modify((p) => {
    if (text) p.notes = notes;
    else delete p.notes;
    p.updatedAt = nowInstant();
  });
}
